//-- Copyright (c) 2021 TMT Insights Group, LLC
import { formatDateBeginOrEnd } from '@/_utils/date';
import { convertStylesToStyled } from '@/_utils/utils';
import { selectCurrentCycle, selectGraphsStatus, selectStatusTotal } from '@/redux/selectors/dashboardSelectors';
import { applyFilters, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import { Card, Typography } from '@mui/material';
import { addDays, addMilliseconds, addMonths, parse } from 'date-fns';
import { gsap } from 'gsap';
import { RefObject, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { selectUser, selectUserDarkMode } from '../../../redux/selectors/authSelectors';
import PolarisIcon from '../../_common/PolarisIcon';
import { StatusCard as StatusCardType } from '@/__configs/config_types';
import { selectJobStages, selectJobStatuses } from '@/redux/selectors/constantsSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { UserProfile } from '@/redux/slices/slice_types';

type StatusCardProps = {
  card: StatusCardType;
  filter?: string[];
  loading?: boolean;
  totalCards?: number;
};
const StatusCard = ({ card, filter, loading = false, totalCards = 5 }: StatusCardProps, ref: any) => {
  const dispatch = useAppDispatch();
  const { editable } = useAppSelector(selectGraphsStatus);
  const history = useHistory();
  const darkMode = useAppSelector(selectUserDarkMode);
  const currentCycle = useAppSelector(selectCurrentCycle);
  const jobStatuses = useAppSelector(selectJobStatuses);
  const jobStages = useAppSelector(selectJobStages);
  const [displayCount, setDisplayCount] = useState<number>(0);
  const theme = useTheme();
  const el = useRef();
  const missingComponentsRef = useRef();
  const currentUser = useAppSelector(selectUser);
  const count = useAppSelector((state) => selectStatusTotal(state, card.status));
  const color = card.color.charAt(0) === '#' ? card.color : (theme?.palette as any)?.[card.color]?.main;

  useEffect(() => {
    return () => gsap.killTweensOf(missingComponentsRef.current as any);
  }, [darkMode]);

  useEffect(() => {
    setDisplayCount(count);
  }, [count]);

  const onClick = () => {
    dispatch(setCurrentTable('jobs'));
    dispatch(clearFilters());
    const jobStageKeys = Object.keys(jobStages);
    const stage_filter = filter?.filter((item) => jobStageKeys.includes(item));

    const filters: { stage_filter?: string[]; date?: any; created_at?: any; status_filter?: string[]; managed_by_filter?: UserProfile[] } = {
      stage_filter,
      status_filter: card.excludeStatus ? Object.keys(jobStatuses).filter((s) => card.excludeStatus?.indexOf(s) === -1) : undefined
    };
    if (typeof currentCycle === 'object') {
      const { startDate, endDate } = currentCycle.calculate();
      filters.created_at = {
        created_at__gte: startDate,
        created_at__lte: endDate,
      }
      filters.managed_by_filter = [currentUser];
    } else if (currentCycle !== 'All') {
      // we store cycle dates as YYYY-MM-02 because TZ was making cycle date show month before. So we add 1 day:
      const due_date__gte = addDays(parse(currentCycle, 'yyyy-MM-dd', new Date()), -1);
      const due_date__lte = addMilliseconds(addMonths(due_date__gte, 1), -1);

      const date = {
        due_date__gte: formatDateBeginOrEnd(due_date__gte, 'start'),
        due_date__lte: formatDateBeginOrEnd(due_date__lte, 'end')
      };

      filters.date = date;
    }
    dispatch(applyFilters({ filters } as any));
    history.push('jobs');
  };

  useImperativeHandle(ref, () => ({
    onUpdate(scale: any) {
      if (!scale) return;
      const newValue = (count * scale)?.toFixed(0);
      setDisplayCount(parseInt(newValue));
    },
    el,
    onComplete() {}
  }));

  return (
    <StatusCardWrapper ref={ref} total={totalCards} loading={+loading} editable={+editable}>
      <StyledCard onClick={onClick}>
        <TopRow>
          <StatusCardLabel color={color} variant="h3">
            {card.name}
          </StatusCardLabel>
          <StyledIcon color={color} icon={card.icon} />
        </TopRow>
        <CountWrapper color={color} ref={el as RefObject<any>}>
          {loading ? '-' : (+displayCount)?.toLocaleString('en-US')}
        </CountWrapper>
      </StyledCard>
    </StatusCardWrapper>
  );
};

export default forwardRef(StatusCard);

const StatusCardWrapper = styled.div<{ total: number; loading: number; editable: number }>`
  position: relative;
  width: 100%;
  transition: ease transform 350ms;
  opacity: ${({ loading, editable }) => (loading || editable ? 0.4 : 1)};

  &:hover {
    cursor: pointer;
    transform: scale(1.03);
    transition: ease transform 350ms;

    & .statusCard {
      border-color: ${({ color }) => color + '6a'};
      transition: ease border 350ms;
    }
  }

  &:last-child {
    margin-right: 0;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    &:hover {
      transform: scale(1.06);
    }
  }
`;

const StyledCard = styled(Card)`
  border: 1px solid transparent;
  padding: ${({ theme }) => theme.spacing(1)};
  position: relative;
  overflow: hidden;
  transition: ease border 350ms;
  box-shadow: 0px 12px 26px 0px rgba(16, 30, 115, 0.06);
  height: 134px;
  ${({ theme }) => convertStylesToStyled(theme.components?.MuiPaper?.styleOverrides?.root)};
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.StatusCard)};
`;

const StatusCardLabel = styled(Typography)<{ color: StatusCardType['color'] }>`
  font-size: 1.1rem;
  color: ${({ color }) => color};
  opacity: 0.8;
  font-weight: 600;
  padding: ${({ theme }) => theme.spacing(0.5, 0, 0, 0.5)};
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.StatusCard?.label)};
`;

const CountWrapper = styled.div<{ color: StatusCardType['color'] }>`
  font-size: 3rem;
  text-align: center;
  color: ${({ color }) => color};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export const StatusIcon = styled.span<{ color: StatusCardType['color'] }>`
  right: ${({ theme }) => theme.spacing(1)};
  border-radius: 3px;
  background: ${({ color }) => color + '2a'};
  color: ${({ color }) => color};
  font-size: 1.5rem;
  padding: ${({ theme }) => theme.spacing(0.5)};
`;

const TopRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  position: absolute;
  width: calc(100% - ${({ theme }) => theme.spacing(2)});
`;

const StyledIcon = styled(PolarisIcon)<{ color: StatusCardType['color'] }>`
  height: 27px;
  width: 27px;
  color: ${({ color }) => color};
  border-radius: 3px;
  background: ${({ color }) => color + '2a'};
  padding: ${({ theme }) => theme.spacing(0.5)};
`;
